import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, Download, FileJson, FileSpreadsheet, Loader2, CheckCircle, Database, Archive } from 'lucide-react';
import { toast } from 'sonner';
import API from '../api';

const DataExport = ({ user, setUser }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({});
  const [exportingAll, setExportingAll] = useState(false);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await API.get('/data-export/collections');
      setCollections(response.data);
    } catch (error) {
      toast.error('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  };

  const exportCollection = async (collectionName, format) => {
    setExporting(prev => ({ ...prev, [`${collectionName}-${format}`]: true }));
    
    try {
      const response = await API.get(`/data-export/${collectionName}?format=${format}`, {
        responseType: 'blob'
      });
      
      const extension = format === 'json' ? 'json' : 'xlsx';
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collectionName}_export.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${collectionName} exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${collectionName}`);
    } finally {
      setExporting(prev => ({ ...prev, [`${collectionName}-${format}`]: false }));
    }
  };

  const exportAllData = async (format) => {
    setExportingAll(true);
    
    try {
      const response = await API.get(`/data-export-all?format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `full_data_export_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('All data exported successfully!');
    } catch (error) {
      toast.error('Failed to export all data');
    } finally {
      setExportingAll(false);
    }
  };

  const hasAccess = useSectionAccess('/data-export');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access data export.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Export</h1>
          <p className="text-gray-500 mt-1">Download your data for backup or migration</p>
        </div>

        {/* Export All Section */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-blue-600" />
              Export All Data
            </CardTitle>
            <CardDescription>
              Download all your data in a single ZIP file. This includes all collections listed below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                onClick={() => exportAllData('json')}
                disabled={exportingAll}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="export-all-json-btn"
              >
                {exportingAll ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileJson className="h-4 w-4 mr-2" />
                )}
                Export All as JSON (ZIP)
              </Button>
              <Button
                onClick={() => exportAllData('excel')}
                disabled={exportingAll}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-100"
                data-testid="export-all-excel-btn"
              >
                {exportingAll ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                )}
                Export All as Excel (ZIP)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Individual Collections */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Individual Collections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.map((collection) => (
              <Card key={collection.name} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="h-4 w-4 text-gray-500" />
                    {collection.label}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {collection.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportCollection(collection.name, 'json')}
                      disabled={exporting[`${collection.name}-json`]}
                      data-testid={`export-${collection.name}-json-btn`}
                    >
                      {exporting[`${collection.name}-json`] ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <FileJson className="h-3 w-3 mr-1" />
                      )}
                      JSON
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportCollection(collection.name, 'excel')}
                      disabled={exporting[`${collection.name}-excel`]}
                      data-testid={`export-${collection.name}-excel-btn`}
                    >
                      {exporting[`${collection.name}-excel`] ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="h-3 w-3 mr-1" />
                      )}
                      Excel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Info Section */}
        <Card className="bg-gray-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Data Security</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Your exported data is downloaded directly to your device. No data is stored on external servers during the export process.
                  Keep your exported files secure and do not share them with unauthorized persons.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DataExport;
